/**
 * InboxList — left sidebar showing paginated message summaries.
 *
 * Features:
 *   - Filter tabs: All / Unread / Starred / Trash
 *   - Bold subject for unread messages
 *   - Row hover action bar: star, read/unread envelope, trash (Restore in Trash)
 *   - Click to select a message (opens ThreadView)
 *   - Relative timestamp (e.g. "2 hours ago")
 */

import React, { useEffect, useState, useCallback, useRef } from 'react';
import { messages as messagesApi, notifications, MessageSummary, ghlContactUrl } from '../../../lib/api.js';
import { useAuth } from '../../../lib/auth-context.js';

interface Props {
  selectedId: string | null;
  onSelect: (message: MessageSummary) => void;
  /** Signal from parent to refresh (e.g. after sending a new message) */
  refreshSignal?: number;
  /**
   * When set, scopes the inbox to a single GHL contact (deep-link from the
   * contact widget: /app/mailbox?contactId={id}). Staff-only on the backend.
   */
  contactId?: string | undefined;
}

type Filter = 'all' | 'unread' | 'starred' | 'trash';

/** Rows fetched per page; "Load more" appends the next page (server caps limit at 100). */
const PAGE_SIZE = 25;

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins  = Math.floor(diff / 60_000);
  const hours = Math.floor(mins / 60);
  const days  = Math.floor(hours / 24);
  if (mins  < 1)  return 'just now';
  if (mins  < 60) return `${mins}m ago`;
  if (hours < 24) return `${hours}h ago`;
  return `${days}d ago`;
}

export default function InboxList({ selectedId, onSelect, refreshSignal, contactId }: Props): React.ReactElement {
  const { user } = useAuth();
  const locationId = user?.locationId ?? '';
  const [items, setItems]         = useState<MessageSummary[]>([]);
  const [unreadCount, setUnread]  = useState(0);
  const [filter, setFilter]       = useState<Filter>('all');
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState<string | null>(null);
  // Row currently under the pointer (shows its action bar) + the row whose
  // status action is in flight (disables that row's action buttons).
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [busyId, setBusyId]       = useState<string | null>(null);
  const [total, setTotal]         = useState(0);
  const [page, setPage]           = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  // Mirror `page` in a ref so the memoized poll / status reload can re-fetch the whole
  // loaded window (not just page 1) without collapsing the list.
  const pageRef = useRef(1);

  // silent=true refreshes in the background (live-update poll) without the
  // "Loading…" flash replacing the visible list.
  // Fetch page `p`: p===1 REPLACES the list, p>1 APPENDS. `limit` lets the silent
  // poll / status reload re-pull the whole loaded window in one shot.
  const load = useCallback(async (opts?: { page?: number; silent?: boolean; limit?: number }) => {
    const p = opts?.page ?? 1;
    const silent = opts?.silent === true;
    const limit = opts?.limit ?? PAGE_SIZE;
    if (!silent && p === 1) setLoading(true);
    setError(null);
    try {
      const params: { contactId?: string; unreadOnly?: boolean; filter?: 'starred' | 'trash'; page?: number; limit?: number } = { page: p, limit };
      if (contactId) params.contactId = contactId;
      if (filter === 'unread') params.unreadOnly = true;
      else if (filter === 'starred') params.filter = 'starred';
      else if (filter === 'trash') params.filter = 'trash';
      const res = await messagesApi.list(params);
      if (res.success && res.data) {
        const rows = res.data.messages;
        setItems((prev) => (p === 1 ? rows : [...prev, ...rows]));
        setTotal(res.data.total);
        setUnread(res.data.unreadCount);
      } else if (!silent) {
        setError(res.error ?? 'Failed to load inbox');
      }
    } catch {
      if (!silent) setError('Failed to load inbox');
    } finally {
      if (!silent && p === 1) setLoading(false);
    }
  }, [filter, contactId]);

  useEffect(() => { setPage(1); pageRef.current = 1; void load({ page: 1 }); }, [load, refreshSignal]);

  const loadMore = useCallback(async () => {
    setLoadingMore(true);
    const next = page + 1;
    await load({ page: next });
    setPage(next);
    pageRef.current = next;
    setLoadingMore(false);
  }, [page, load]);

  // Live updates: poll the lightweight counts endpoint (no PHI, no audit row)
  // and reload the list only when the location-wide staff-unread count CHANGES
  // between polls — so a new patient reply shows up without a manual refresh.
  // Compared against the poll's own previous value (not the list's unreadCount,
  // which is contact-scoped when the deep-link filter is active and would never
  // match the location-wide number). Paused while the tab is hidden.
  const lastPolledUnread = useRef<number | null>(null);

  useEffect(() => {
    const timer = setInterval(async () => {
      if (document.hidden) return;
      try {
        const res = await notifications.counts();
        if (!res.success || !res.data) return;
        const current = res.data.messageUnread;
        if (lastPolledUnread.current !== null && current !== lastPolledUnread.current) {
          await load({ page: 1, silent: true, limit: Math.min(100, pageRef.current * PAGE_SIZE) });
        }
        lastPolledUnread.current = current;
      } catch { /* transient poll failure — next tick retries */ }
    }, 30_000);
    return () => clearInterval(timer);
  }, [load]);

  // Run a status mutation optimistically, then silently reload so the server
  // reconciles the list (a reload can never leave a stale row). A failed call
  // is swallowed — the reload restores truth either way.
  const runStatus = useCallback((mutate: () => Promise<unknown>, messageId: string) => {
    setBusyId(messageId);
    void (async () => {
      try { await mutate(); } catch { /* reload reconciles */ }
      // Reconcile the whole loaded window (not just page 1) so "Load more" rows survive.
      finally { setBusyId(null); void load({ page: 1, silent: true, limit: Math.min(100, pageRef.current * PAGE_SIZE) }); }
    })();
  }, [load]);

  const toggleStar = (e: React.MouseEvent, msg: MessageSummary) => {
    e.stopPropagation();
    const next = !msg.starredAt;
    setItems((prev) =>
      filter === 'starred' && !next
        ? prev.filter((m) => m.id !== msg.id)
        : prev.map((m) => (m.id === msg.id ? { ...m, starredAt: next ? new Date().toISOString() : null } : m)),
    );
    runStatus(() => messagesApi.setStatus(msg.id, { starred: next }), msg.id);
  };

  const toggleReadState = (e: React.MouseEvent, msg: MessageSummary) => {
    e.stopPropagation();
    const targetRead = msg.staffUnread; // currently unread → mark read, else unread
    setItems((prev) =>
      filter === 'unread' && targetRead
        ? prev.filter((m) => m.id !== msg.id)
        : prev.map((m) => (m.id === msg.id ? { ...m, staffUnread: !targetRead } : m)),
    );
    runStatus(() => messagesApi.setReadStatus(msg.id, targetRead), msg.id);
  };

  const toggleTrash = (e: React.MouseEvent, msg: MessageSummary) => {
    e.stopPropagation();
    const toTrash = filter !== 'trash'; // Trash tab → Restore
    setItems((prev) => prev.filter((m) => m.id !== msg.id));
    runStatus(() => messagesApi.setStatus(msg.id, { deleted: toTrash }), msg.id);
  };

  return (
    <div style={styles.container}>
      {/* Filter tabs */}
      <div style={styles.tabs}>
        <button
          style={{ ...styles.tab, ...(filter === 'all' ? styles.tabActive : {}) }}
          onClick={() => setFilter('all')}
        >
          All
        </button>
        <button
          style={{ ...styles.tab, ...(filter === 'unread' ? styles.tabActive : {}) }}
          onClick={() => setFilter('unread')}
        >
          Unread{unreadCount > 0 && <span style={styles.badge}>{unreadCount}</span>}
        </button>
        <button
          style={{ ...styles.tab, ...(filter === 'starred' ? styles.tabActive : {}) }}
          onClick={() => setFilter('starred')}
        >
          Starred
        </button>
        <button
          style={{ ...styles.tab, ...(filter === 'trash' ? styles.tabActive : {}) }}
          onClick={() => setFilter('trash')}
        >
          Trash
        </button>
      </div>

      {/* List */}
      <div style={styles.list}>
        {loading && <p style={styles.status}>Loading…</p>}
        {!loading && error && <p style={{ ...styles.status, color: '#c0392b' }}>{error}</p>}
        {!loading && !error && items.length === 0 && (
          <div style={styles.emptyState}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: 10 }}>
              <rect x="2" y="4" width="20" height="16" rx="2"/>
              <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
            </svg>
            <p style={styles.emptyTitle}>
              {filter === 'unread' ? 'No unread messages'
                : filter === 'starred' ? 'No starred messages'
                : filter === 'trash' ? 'Trash is empty'
                : 'Inbox is empty'}
            </p>
            <p style={styles.emptyDesc}>
              {filter === 'unread' ? 'All messages have been read.'
                : filter === 'starred' ? 'Star a message to keep it here.'
                : filter === 'trash' ? 'Trashed messages appear here.'
                : 'Patient messages will appear here when received.'}
            </p>
          </div>
        )}
        {items.map((msg) => {
          const isSelected = msg.id === selectedId;
          const isUnread   = msg.staffUnread;
          const isStarred  = !!msg.starredAt;
          const inTrash    = filter === 'trash';
          const showActions = hoveredId === msg.id;
          const rowBusy    = busyId === msg.id;
          return (
            <div
              key={msg.id}
              style={{ ...styles.row, ...(isSelected ? styles.itemSelected : {}) }}
              onMouseEnter={() => setHoveredId(msg.id)}
              onMouseLeave={() => setHoveredId((h) => (h === msg.id ? null : h))}
            >
              <button style={styles.item} onClick={() => onSelect(msg)}>
                <div style={styles.itemTop}>
                  <span style={{ ...styles.subject, fontWeight: isUnread ? 700 : 400 }}>
                    {msg.subject}
                  </span>
                  <span style={styles.time}>{relativeTime(msg.createdAt)}</span>
                </div>
                <div style={styles.itemMeta}>
                  <span style={styles.metaText}>
                    {'To: '}
                    <a
                      href={ghlContactUrl(locationId, msg.toContactGhlId)}
                      target="_blank"
                      rel="noreferrer"
                      style={styles.contactLink}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {msg.toContactName || msg.toContactGhlId.slice(0, 12) + '…'}
                    </a>
                  </span>
                  {msg.hasAttachments && (
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 18 8.84l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
                    </svg>
                  )}
                  {isUnread && <span style={styles.dot} />}
                </div>
              </button>

              {showActions && (
                <div style={styles.actionBar}>
                  {!inTrash && (
                    <>
                      <button
                        style={{ ...styles.actionBtn, ...(isStarred ? styles.actionBtnStar : {}) }}
                        onClick={(e) => toggleStar(e, msg)}
                        disabled={rowBusy}
                        title={isStarred ? 'Unstar' : 'Star'}
                        aria-label={isStarred ? 'Unstar' : 'Star'}
                      >
                        <svg width="15" height="15" viewBox="0 0 24 24" fill={isStarred ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                        </svg>
                      </button>
                      <button
                        style={{ ...styles.actionBtn, ...(isUnread ? styles.actionBtnUnread : {}) }}
                        onClick={(e) => toggleReadState(e, msg)}
                        disabled={rowBusy}
                        title={isUnread ? 'Mark as read' : 'Mark as unread'}
                        aria-label={isUnread ? 'Mark as read' : 'Mark as unread'}
                      >
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <rect x="2" y="4" width="20" height="16" rx="2" />
                          <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                        </svg>
                      </button>
                    </>
                  )}
                  <button
                    style={styles.actionBtn}
                    onClick={(e) => toggleTrash(e, msg)}
                    disabled={rowBusy}
                    title={inTrash ? 'Restore' : 'Trash'}
                    aria-label={inTrash ? 'Restore' : 'Trash'}
                  >
                    {inTrash ? (
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                        <path d="M3 3v5h5" />
                      </svg>
                    ) : (
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M3 6h18" />
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                        <line x1="10" x2="10" y1="11" y2="17" />
                        <line x1="14" x2="14" y1="11" y2="17" />
                      </svg>
                    )}
                  </button>
                </div>
              )}
            </div>
          );
        })}
        {!loading && !error && total > items.length && (
          <button style={styles.loadMore} onClick={() => void loadMore()} disabled={loadingMore}>
            {loadingMore ? 'Loading…' : `Load more (${items.length} of ${total})`}
          </button>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Inline styles (no external CSS — keeps the component self-contained)
// ---------------------------------------------------------------------------

const styles: Record<string, React.CSSProperties> = {
  container: {
    display:    'flex',
    flexDirection: 'column',
    height:     '100%',
    borderRight: '1px solid #e5e7eb',
    background: '#fff',
  },
  tabs: {
    display:    'flex',
    borderBottom: '1px solid #e5e7eb',
    padding:    '0 8px',
  },
  tab: {
    flex:        1,
    padding:     '10px 4px',
    border:      'none',
    background:  'transparent',
    cursor:      'pointer',
    fontWeight:  500,
    fontSize:    13,
    color:       '#6b7280',
    display:     'flex',
    alignItems:  'center',
    justifyContent: 'center',
    gap:         4,
    whiteSpace:  'nowrap',
    // Every tab reserves the 2px underline (transparent) so the active tab toggles it to
    // blue with NO layout shift and the active state reads unambiguously.
    borderBottom: '2px solid transparent',
  },
  tabActive: {
    color:       '#2563eb',
    borderBottom: '2px solid #2563eb',
  },
  loadMore: {
    display: 'block', width: 'calc(100% - 24px)', margin: '8px 12px 16px',
    padding: '8px', background: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: 6,
    cursor: 'pointer', fontSize: 12, fontWeight: 600, color: '#374151',
  },
  badge: {
    background:  '#ef4444',
    color:       '#fff',
    borderRadius: '999px',
    fontSize:    11,
    fontWeight:  700,
    padding:     '1px 6px',
    minWidth:    18,
    textAlign:   'center',
  },
  list: {
    flex:        1,
    overflowY:   'auto',
  },
  status: {
    padding:     '24px 16px',
    color:       '#6b7280',
    fontSize:    14,
    textAlign:   'center',
  },
  emptyState: {
    display:        'flex',
    flexDirection:  'column',
    alignItems:     'center',
    padding:        '40px 20px',
    textAlign:      'center',
  },
  emptyTitle: {
    margin:     '0 0 6px',
    fontSize:   14,
    fontWeight: 600,
    color:      '#374151',
  },
  emptyDesc: {
    margin:     0,
    fontSize:   13,
    color:      '#9ca3af',
    lineHeight: 1.5,
  },
  row: {
    position:     'relative',
    borderBottom: '1px solid #f3f4f6',
  },
  item: {
    display:     'block',
    width:       '100%',
    padding:     '12px 16px',
    border:      'none',
    background:  'transparent',
    cursor:      'pointer',
    textAlign:   'left',
  },
  itemSelected: {
    background:  '#eff6ff',
  },
  actionBar: {
    position:     'absolute',
    top:          8,
    right:        10,
    display:      'flex',
    gap:          2,
    background:   '#fff',
    border:       '1px solid #e5e7eb',
    borderRadius: 6,
    padding:      2,
    boxShadow:    '0 1px 3px rgba(0,0,0,0.08)',
  },
  actionBtn: {
    background:    'transparent',
    border:        'none',
    borderRadius:  4,
    padding:       4,
    cursor:        'pointer',
    color:         '#6b7280',
    display:       'inline-flex',
    alignItems:    'center',
    justifyContent:'center',
    lineHeight:    0,
  },
  actionBtnStar:   { color: '#f59e0b' },
  actionBtnUnread: { color: '#2563eb' },
  itemTop: {
    display:        'flex',
    justifyContent: 'space-between',
    alignItems:     'flex-start',
    gap:            8,
    marginBottom:   4,
  },
  subject: {
    fontSize:    14,
    color:       '#111827',
    lineHeight:  1.4,
    flex:        1,
  },
  time: {
    fontSize:    12,
    color:       '#9ca3af',
    whiteSpace:  'nowrap',
  },
  itemMeta: {
    display:    'flex',
    alignItems: 'center',
    gap:        6,
  },
  metaText: {
    fontSize:   12,
    color:      '#9ca3af',
    flex:       1,
  },
  paperclip: {
    fontSize:   12,
  },
  contactLink: { color: '#6b7280', textDecoration: 'underline', textDecorationStyle: 'dotted' as const },
  dot: {
    width:       8,
    height:      8,
    borderRadius: '50%',
    background:  '#3b82f6',
    flexShrink:  0,
  },
};
